'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Check } from 'phosphor-icons';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Button from '../../atoms/Button';
import { Textarea } from '../../atoms/Textarea';
import { Banner } from '../../molecules/Banner';
import { FormModal } from '../../molecules/FormModal/FormModal';
import { RichText } from '../../molecules/RichText';
import {
  MAX_REPORT_REVIEW_CHARS,
  reportReviewFormSchema,
  type ReportReviewModalData,
  type ReportReviewModalProps,
} from './ReportReviewModal.types';

export const ReportReviewModal = ({
  reviewId,
  microcopy,
  onSubmitReport,
  className = '',
}: ReportReviewModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [showServerMessage, setShowServerMessage] = useState(false);
  const serverMessageRef = useRef<HTMLDivElement>(null);
  const emptyReportData = { reportReview: '', reportType: '' };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, errors },
  } = useForm<{ reportReview: string; reportType: string }>({
    resolver: zodResolver(reportReviewFormSchema),
    mode: 'onChange',
    defaultValues: emptyReportData,
  });

  const onFormSubmit = async (reportData: ReportReviewModalData) => {
    if (isSubmitting) return;
    setShowServerMessage(false);
    setIsSubmitting(true);
    try {
      await onSubmitReport(reviewId, reportData);
      setShowConfirmationMessage(true);
    } catch (error) {
      console.error('Error submitting report:', error);
      setShowServerMessage(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset(emptyReportData);
    setShowServerMessage(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      reset(emptyReportData);
      setShowServerMessage(false);
      setIsSubmitting(false);
      setShowConfirmationMessage(false);
    }
  };

  const handleOnDismiss = () => {
    setShowServerMessage(false);
  };

  const triggerElement = (
    <Button.v2
      size="xsmall"
      shape="square"
      variant="tertiary-grey-monochrome"
      label={microcopy.triggerLabel}
      data-testid="report-review-button"
    />
  );

  const renderModalForm = (
    <>
      <RichText className="mt-4" html={microcopy.description} />
      <Controller
        control={control}
        name="reportType"
        render={({ field }) => (
          <div>
            <label className="text-sm leading-5 tracking-tight text-grey-900">
              {microcopy.reportType.title}
            </label>
            <ul className="mt-2">
              {microcopy.reportType.options.map((option) => (
                <li key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    id={`report-type-${option.value}`}
                    name="reportType"
                    value={option.value}
                    checked={field.value === option.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    className="body-small h-4 w-4 border-grey-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label
                    htmlFor={`report-type-${option.value}`}
                    className="ml-2 block text-grey-900"
                  >
                    {option.label}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}
      />
      <Controller
        control={control}
        name="reportReview"
        render={({ field }) => (
          <>
            <label
              htmlFor="report-review-input"
              className="mb-2 mt-5 text-sm font-normal leading-5 tracking-tight text-grey-900"
            >
              {microcopy.descriptionTitle}
            </label>
            <Textarea
              id="report-review-input"
              data-testid="report-review-input"
              placeholder={microcopy.placeholder}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              maxLength={MAX_REPORT_REVIEW_CHARS}
              showRemainingChars
              className="h-full min-h-24 w-full flex-1 resize-none overflow-auto rounded-lg border border-grey-300 px-3 py-2"
              aria-invalid={Boolean(errors.reportReview)}
            />
          </>
        )}
      />
    </>
  );

  const renderConfirmationMessage = (
    <div className="flex min-h-[400px] flex-1 flex-col items-center justify-center md:min-h-[500px] md:px-4">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-grey-100 text-center">
        <Check weight="bold" className="mx-auto" size={18} />
      </div>
      <p className="mt-5 text-2xl font-semibold">
        {microcopy.confirmationMessage.title}
      </p>
      <p className="text-sm">{microcopy.confirmationMessage.subtitle}</p>
    </div>
  );

  const modalContent = (
    <div className="flex h-full flex-col pb-4">
      {showServerMessage && microcopy.serverMessage && (
        <div ref={serverMessageRef} className="my-5">
          <Banner
            title={microcopy.serverMessage.title}
            description={microcopy.serverMessage.description}
            dismiss={microcopy.serverMessage.dismiss}
            onDismiss={handleOnDismiss}
            cta={microcopy.serverMessage.cta || ''}
            style="fill"
          />
        </div>
      )}
      {showConfirmationMessage ? renderConfirmationMessage : renderModalForm}
    </div>
  );

  return (
    <FormModal
      size="medium"
      open={isOpen}
      trigger={triggerElement}
      content={modalContent}
      title={microcopy.title}
      descriptionClassName="leading-6 tracking-[-0.006em]"
      close={microcopy.close}
      submit={showConfirmationMessage ? '' : microcopy.primaryCta}
      disabled={!isValid || isSubmitting}
      cancel={showConfirmationMessage ? '' : microcopy.secondaryCta}
      onSubmit={handleSubmit(onFormSubmit)}
      onCancel={handleCancel}
      onOpenChange={handleOpenChange}
      className={className}
      support={{
        title: microcopy.support.title,
        description: microcopy.support.description,
      }}
      isStickyFooter={false}
    />
  );
};
